import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays } from 'lucide-react';
import { api } from '../lib/api';
import type { BlogPost } from '../types';
import { formatDate } from '../lib/format';
import { Spinner, Alert } from '../components/ui';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api
      .get<BlogPost>(`/blog/${slug}`)
      .then(setPost)
      .catch(() => setError('Post not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-32"><Spinner label="Loading post..." /></div>;
  if (error || !post) {
    return (
      <div className="pt-32 max-w-3xl mx-auto px-4 space-y-6">
        <Alert>{error || 'Post not found'}</Alert>
        <Link to="/blog" className="inline-flex items-center bg-primary text-black px-6 py-2 font-semibold hover:bg-primary-light transition-colors">
          <ArrowLeft className="mr-2 h-5 w-5" /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[420px] overflow-hidden">
        {post.cover_image && (
          <img
            src={post.cover_image}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-end pb-16">
          <div className="text-white max-w-3xl">
            <div className="flex items-center text-xs md:text-sm mb-3">
              <span className="flex items-center">
                <CalendarDays className="h-4 w-4 mr-1.5 text-primary" /> {formatDate(post.created_at)}
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight capitalize">{post.title}</h1>
            {post.excerpt && (
              <p className="mt-4 text-ash-light text-lg md:text-xl leading-relaxed">{post.excerpt}</p>
            )}
          </div>
        </div>
      </section>

      <div className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Link to="/blog" className="inline-flex items-center text-primary font-semibold mb-8 hover:underline">
            <ArrowLeft className="mr-2 h-5 w-5" /> Back to Blog
          </Link>
          <article className="text-charcoal leading-relaxed text-lg whitespace-pre-line">
            {post.content}
          </article>
        </div>
      </div>
    </div>
  );
}
